import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '../theme';

const ThemeContext = createContext();

export function useThemeMode() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children, interfaceLayout = 'Compact' }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved === 'dark' ? 'dark' : 'light';
  });

  // Syncfusion reads its palette from CSS variables scoped to `.e-dark-mode`. It goes on <body>
  // rather than the grid itself so the popups Syncfusion portals into <body> (filter menu, column
  // menu, page-size dropdown) are themed too.
  useEffect(() => {
    document.body.classList.toggle('e-dark-mode', mode === 'dark');
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  const theme = useMemo(() => createAppTheme(mode, interfaceLayout), [mode, interfaceLayout]);

  // `interfaceLayout` rides along so components that style non-MUI widgets (AppGrid) can pick a
  // density without re-deriving it from theme internals.
  const value = useMemo(() => ({ mode, toggleTheme, interfaceLayout }), [mode, interfaceLayout]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
